import React from 'react';
import { motion } from 'framer-motion';
import { Video, Users, MessageCircle, Monitor, Shield, ArrowRight } from 'lucide-react';
import { useQuote } from './QuoteContext';

const CloudVCSection = () => {
  const { openQuote } = useQuote();

  const features = [
    { Icon: Video, title: 'HD Video Meetings', desc: 'Crystal-clear 4K video conferencing for teams of any size.' },
    { Icon: Users, title: 'Up to 1500 Participants', desc: 'Host large webinars and meetings without compromise.' },
    { Icon: Monitor, title: 'Screen Sharing', desc: 'Collaborate in real-time with screen sharing and remote control.' },
    { Icon: MessageCircle, title: 'Chat & Recording', desc: 'Persistent chat, meeting recording and instant playback.' },
    { Icon: Shield, title: 'Enterprise Security', desc: 'End-to-end encryption, on-premise or cloud deployment.' }
  ];

  return (
    <section id="solutions" className="section-padding section-white relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-sky-50/40 via-white to-indigo-50/40 pointer-events-none" />
      <div className="container-x relative">
        <div className="grid lg:grid-cols-2 gap-14 items-center">
          {/* Left content */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <div className="eyebrow-light mb-4"><Video size={12} /> Cloud VC Solutions</div>
            <h2 className="section-title-dark">
              <span className="gradient-text">TrueConf</span> Video Conferencing
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              TrueConf is a video conferencing and collaboration platform designed for business and enterprise environments. It provides high-quality video meetings, webinars, and virtual collaboration — on-premise or delivered from DC10G's cloud.
            </p>
            <p className="mt-3 text-slate-600 leading-relaxed">
              TrueConf offers a comprehensive video conferencing solution that supports one-on-one calls, group meetings, and large-scale webinars with end-to-end encryption.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <button onClick={() => openQuote({ title: 'TrueConf Cloud VC Quote', subtitle: 'Tell us about your video conferencing needs and we\u2019ll send a personalized proposal.', service: 'TrueConf Video Conferencing' })} className="btn-primary">
                Get TrueConf Quote <ArrowRight size={16} />
              </button>
              <a href="#partners" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-white border border-sky-200 text-sky-700 font-semibold hover:bg-sky-50 hover:border-sky-400 transition-all">
                View All Partners
              </a>
            </div>
          </motion.div>

          {/* Right visual - Video call mockup */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
            <div className="relative aspect-video rounded-3xl overflow-hidden bg-gradient-to-br from-sky-500 to-blue-700 p-4 shadow-2xl shadow-sky-500/30">
              <div className="grid grid-cols-2 gap-3 h-full">
                {[
                  { color: 'from-cyan-400 to-teal-500' },
                  { color: 'from-emerald-400 to-green-500' },
                  { color: 'from-amber-400 to-orange-500' },
                  { color: 'from-rose-400 to-pink-500' }
                ].map((p, i) => (
                  <motion.div
                    key={i}
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{ duration: 3, delay: i * 0.5, repeat: Infinity }}
                    className={`rounded-2xl bg-gradient-to-br ${p.color} flex items-center justify-center relative overflow-hidden`}
                  >
                    <div className="w-14 h-14 rounded-full bg-white/30 flex items-center justify-center">
                      <Users className="text-white" size={26} />
                    </div>
                    <div className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded bg-black/40 backdrop-blur">
                      <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                      <span className="text-[10px] text-white font-mono">LIVE</span>
                    </div>
                  </motion.div>
                ))}
              </div>
              {/* Control bar */}
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900/80 backdrop-blur">
                {[Video, MessageCircle, Monitor].map((I, i) => (
                  <div key={i} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><I className="text-white" size={14} /></div>
                ))}
                <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center"><Video className="text-white" size={14} /></div>
              </div>
            </div>

            {/* Floating brand */}
            <div className="absolute -top-4 -right-4 px-4 py-2 rounded-xl bg-white shadow-xl border border-sky-100 flex items-center gap-2">
              <Video className="text-sky-600" size={18} />
              <span className="font-bold text-slate-900">TrueConf</span>
            </div>
          </motion.div>
        </div>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4 mt-14">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="p-5 rounded-2xl glass-light-card group"
            >
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 flex items-center justify-center mb-3 shadow-md shadow-sky-500/20 group-hover:scale-110 transition-transform">
                <f.Icon className="text-white" size={18} />
              </div>
              <div className="font-bold text-slate-900 text-sm">{f.title}</div>
              <div className="text-xs text-slate-600 mt-1">{f.desc}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CloudVCSection;
